/**
 * PCO command center page — tabbed since 2026-05-14 to keep Joey out of
 * the mile-long scroll the original layout produced. Header band + funding
 * summary card are always visible above the tab bar so the headline numbers
 * stay in view; everything else slices into three tabs:
 *
 *   • Weekly     — labeled "Weekly Rollups". The work surface and default
 *                  landing: packages with their week tables, Start Next.
 *   • Cumulative — labeled "Totals" in the UI (Finding #11 — "Cumulative"
 *                  didn't land with PM users). Funding buckets card on top,
 *                  then Labor / Equipment / Materials / Subcontractors /
 *                  Invoice tables + inline materials queue. Verification
 *                  surface. Internal id/hash/route stay `cumulative` — only
 *                  the visible tab label changed.
 *   • History    — labeled "Sources" in the UI. Completed rollups, source
 *                  PDFs, mapped activity codes, reconciliation (stub until
 *                  the standalone /reconciliation route is decommissioned).
 *
 * (An Overview tab held funding + a duplicate activity-codes card until
 * 2026-08-05; see the PcoTab comment below.)
 *
 * URL hash drives the active tab (#weekly default — the work surface;
 * #cumulative, #history; stale #overview links fall through to the default)
 * so deep links + back button work. A single GET /api/rollups/pco/:pcoId/
 * cumulative call still powers the breakdown + sources + codes; the
 * weekly-rollups list is filtered client-side from useWeeklyRollups.
 */
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { usePcoCumulative, usePcoGeneratedDocuments, useWeeklyRollups } from '../lib/useRollups';
import { Container, ExpandChevron, Skeleton, StaleDataBanner } from '../components/design';
import { PdfPreviewModal } from '../components/PdfPreviewModal';
import { PdfCompareModal } from '../components/PdfCompareModal';
import {
  generatedDocumentCell,
  type GeneratedColumnStatus,
  type SourceComparePair,
} from '../lib/regenerationPlan';
import { WeeklyRollupsTab } from '../components/WeeklyRollupsTab';
import { ConfirmModal } from '../components/ConfirmModal';
import { EditPcoModal } from '../components/EditPcoModal';
import { PcoFundingSection } from '../components/PcoFundingSection';
import { useSettingsPcos } from '../lib/useSettings';
import { usePcoTranches } from '../lib/usePcos';
import { usePcoSubmissions } from '../lib/useSubmissions';
import {
  formatDateMd,
  formatDateShort,
  formatHours,
  formatMoneyCents,
  formatPcoNumber,
  formatWeekRange,
  gcName,
} from '../lib/format';
import { fundingCardLead, proposedDisplayCents } from '../lib/pcoMoney';
import {
  derivePdfFilename,
  type PcoCumulativeContributingPdf,
  type PcoGeneratedDocument,
  type PcoStatus,
} from '@coffman-tm/shared';

/* Overview retired 2026-08-05 (Brandon: it held only a duplicate of the
   Sources activity-code card plus Funding, which is money and belongs with
   money). Funding now tops the Totals tab. Stale #overview deep links fall
   through to the default below. */
type PcoTab = 'weekly' | 'cumulative' | 'history';

const PCO_TAB_HASH: Record<PcoTab, string> = {
  weekly: '#weekly',
  cumulative: '#cumulative',
  history: '#history',
};

function pcoTabFromHash(hash: string): PcoTab {
  switch (hash) {
    case '#cumulative':
      return 'cumulative';
    case '#history':
      return 'history';
    case '#weekly':
    default:
      // Weekly Rollups is the default landing: it's the work surface (the
      // PDF-inbox flow arrives here to bill the week). The headline money
      // lives in the always-visible summary card anyway.
      return 'weekly';
  }
}

const ACTIVE_STATUSES: readonly PcoStatus[] = ['open', 'active', 'pending_approval', 'approved'];
const COMPLETE_STATUSES: readonly PcoStatus[] = ['paid', 'void'];

export function PcoDetailPage() {
  const { pcoId } = useParams<{ pcoId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { token, me } = useAuth();
  const { refetchNav } = useOutletContext<{ refetchNav: () => void }>();
  const cumulative = usePcoCumulative(pcoId ?? null);
  const rollups = useWeeklyRollups();
  const settingsPcos = useSettingsPcos();
  // Submissions are owned here so the tab-bar status strip and the Weekly
  // Rollups tab read one list — a mutation in the tab refetches both.
  const subs = usePcoSubmissions(pcoId ?? null);
  // Funding tranches are owned here so both the read-only Funding section and
  // the Edit Details bucket editor read one list — a save refetches once.
  const tranches = usePcoTranches(pcoId ?? null);
  const [previewPdfId, setPreviewPdfId] = useState<string | null>(null);
  // Original-vs-generated side by side. Holds the pair rather than a boolean so
  // one modal serves every row of the Sources table, same shape as the inbox's
  // duplicate compare.
  const [comparePair, setComparePair] = useState<SourceComparePair | null>(null);
  const [editOpen, setEditOpen] = useState(false);

  // Opening a PCO counts as reviewing the blocks that landed on it: clear the
  // attention flag (ADR 0004 workflow nudge) so the rail's ranking relaxes,
  // then refresh the rail to reflect it immediately.
  useEffect(() => {
    if (!token || !pcoId) return;
    void api.pcos
      .markReviewed(token, pcoId)
      .then(() => refetchNav())
      .catch(() => {});
  }, [pcoId, token, refetchNav]);

  const activeTab = pcoTabFromHash(location.hash);
  const setTab = (t: PcoTab) =>
    navigate({ hash: PCO_TAB_HASH[t] }, { replace: true });

  // Regenerated copies of the source PDFs. Fetched only while the Sources tab
  // is open — it is a second round trip and the other three tabs never read it.
  const generatedDocs = usePcoGeneratedDocuments(
    activeTab === 'history' ? pcoId ?? null : null,
  );
  const generatedByDaily = new Map<string, PcoGeneratedDocument>(
    (generatedDocs.data?.documents ?? []).map((doc) => [doc.daily_pdf_id, doc]),
  );
  // Only 'ready' may render "—", which is the claim "never part of a send".
  // Both other states blank the cell instead: not knowing is not the same
  // answer as knowing the daily never went out.
  const generatedStatus: GeneratedColumnStatus = generatedDocs.error
    ? 'error'
    : generatedDocs.data === null
      ? 'loading'
      : 'ready';

  if (cumulative.isLoading || !cumulative.data) {
    // Nothing to show yet. If the fetch already failed, say so and offer a way
    // out — a bare "Loading…" over a dead request is a trap.
    const failed = !!cumulative.error && !cumulative.isLoading;
    return (
      <Container>
        <div className="page-head">
          <div>
            <div className="eyebrow">PCO detail</div>
            {failed ? (
              <h1 className="page-title">Couldn’t load this PCO</h1>
            ) : (
              <h1 className="page-title">
                <Skeleton style={{ width: 280, height: 24 }} />
              </h1>
            )}
          </div>
        </div>
        {cumulative.error ? (
          <>
            <p style={{ color: 'var(--danger)', fontSize: 13 }}>{cumulative.error.message}</p>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => void cumulative.refetch()}
            >
              Retry
            </button>
          </>
        ) : (
          // Shaped like the summary card it becomes, not like the five-stat
          // row it used to be — a skeleton that resolves into a different
          // layout reads as the page jumping.
          <div className="section section--card" style={{ marginBottom: 12 }} aria-hidden>
            <div className="pco-summary">
              {[0, 1, 2].map((i) => (
                <div key={i}>
                  <Skeleton style={{ width: 90, height: 11 }} />
                  {/* The third column is Remaining — one value, no hero. */}
                  <Skeleton
                    style={{ width: i === 2 ? 110 : 150, height: i === 2 ? 17 : 30, marginTop: 6 }}
                  />
                  {i !== 2 && <Skeleton style={{ width: 110, height: 13, marginTop: 14 }} />}
                </div>
              ))}
            </div>
          </div>
        )}
      </Container>
    );
  }

  const d = cumulative.data;
  // OT/DT columns only render when the project bills the premium split. When
  // off (Coffman's flat composite rate), a single Total hrs column shows.
  const showOtDt = d.calculate_ot_dt;
  const isComplete = (COMPLETE_STATUSES as readonly string[]).includes(d.pco.status);
  // Same bucket list the server marks up (compute.ts / cumulative.ts): labor +
  // equipment + materials + subcontractor lumps. Leaving subs out here is what
  // hid a $683,814 vendor invoice from this ladder until 2026-07-28.
  const subtotal =
    d.totals.labor_cents +
    d.totals.equipment_cents +
    d.totals.materials_cents +
    d.totals.subcontractors_cents;

  // Derived money fields come from the PCO list endpoint (post-ADR 0002).
  // Falls back to zeros if the list hasn't loaded yet — keeps the page
  // rendering on first paint.
  const pcoMoney = (settingsPcos.data ?? []).find((p) => p.id === d.pco.id);
  const proposed = pcoMoney?.amount_proposed_cents ?? null;
  const approved = pcoMoney?.approved_total_cents ?? 0;
  const pending = pcoMoney?.pending_cents ?? 0;
  const pendingBucketCents = pcoMoney?.pending_bucket_cents ?? 0;
  const billed = pcoMoney?.billed_to_date_cents ?? d.totals.invoice_total_cents;
  // Approved funding − submitted, server-clamped at zero. Null until the PCO
  // list loads; the stat dashes rather than showing a bogus 0.
  const remaining = pcoMoney?.remaining_cents ?? null;
  // Same rule the PCO Log grid's Proposed column uses — one helper, so the
  // two surfaces can't disagree about one PCO.
  const proposedDisplay = proposedDisplayCents({
    amount_proposed_cents: proposed,
    approved_total_cents: approved,
    pending_bucket_cents: pendingBucketCents,
  });
  const hasApprovedBudget = approved > 0;
  const pctBurn = hasApprovedBudget ? (billed / approved) * 100 : 0;
  // "064 silence" (2026-08-06 live review): a PM saved a bucket that landed
  // Pending-with-a-MOD, correctly, and the card's Approved-led hero showed a
  // bare dash for a PCO that plainly had money moving — read as data loss.
  // A PCO that is genuinely all-Pending promotes Pending approval into the
  // lead slot instead; Approved keeps the lead everywhere else, including the
  // true empty case (both zero).
  const fundingLead = fundingCardLead(approved, pending);
  // Only the two states the money can't express. Everything else about this
  // PCO is already legible from the summary card below.
  const lifecycleChip =
    d.pco.status === 'paid' ? 'Paid' : d.pco.status === 'void' ? 'Void' : null;
  const hasActivity =
    d.weeks_with_activity > 0 ||
    billed > 0 ||
    approved > 0 ||
    proposed != null;

  return (
    <Container>
      {/* A failed refresh with data already on screen used to be invisible.
          Keyed on the error so a fresh failure re-announces itself even if a
          previous one was dismissed. */}
      <StaleDataBanner
        key={cumulative.error?.message}
        error={cumulative.error}
        onRetry={() => void cumulative.refetch()}
      />
      {/* ===== Always-visible header band =====
          Page identity is the PCO number; the description folds into the
          subline under it (same inversion the rollup detail page runs). No
          status chip and no aggregate MOD/FWA line here (client, 2026-08-01):
          each submission row carries its own MOD/FWA, and the derived status
          reads off the money in the PCO Log and its drawer. */}
      <div className="page-head">
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h1 className="page-title">PCO {formatPcoNumber(d.pco.pco_number)}</h1>
            {/* LIFECYCLE only. The DERIVED funding chip (Proposed / Partially
                funded / Funded) prints in the PCO Log drawer and nowhere else
                (client, 2026-08-01) — this page answers that question with the
                money itself. Paid and Void are the two states the money can't
                express, which is exactly the rule the PCO Log's inline tags
                follow. */}
            {lifecycleChip && (
              <span className={`status-chip s-${d.pco.status}`}>{lifecycleChip}</span>
            )}
          </div>
          {d.pco.description && <p className="page-sub">{d.pco.description}</p>}
        </div>
        <div className="page-head-actions">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => setEditOpen(true)}
          >
            Edit details
          </button>
          <PcoStatusButton
            pcoId={d.pco.id}
            pcoNumber={d.pco.pco_number}
            currentStatus={d.pco.status}
            isComplete={isComplete}
            onChanged={async () => {
              // The rail renders from AppShell's own PCO list — refresh it too
              // or a completed PCO sits stale in the nav until navigation.
              refetchNav();
              await Promise.all([cumulative.refetch(), rollups.refetch(), settingsPcos.refetch()]);
            }}
          />
        </div>
      </div>

      {/* ===== Always-visible money stats + burn =====
          Five numbers, two directions of money. "Proposed" / "Approved
          funding" / "Pending approval" are the funding conversation — what
          Coffman asked for, what Hoffman executed a MOD/FWA for, what's
          still waiting. "Submitted to date" / "Remaining" are the billing
          conversation — what has gone out the door and what's left under
          the ceiling. The sent number used to read "Approved to date";
          that phrase means approved FUNDING in Coffman's own PCO Log, the
          opposite direction of money, so it's retired here and everywhere
          else in the UI. Same vocabulary in PcoLogPage's strip + PcoDrawer. */}
      {hasActivity ? (
        <div className="section section--card" style={{ marginBottom: 12 }}>
          <div className="pco-summary">
            <div>
              {/* Lead slot: Approved funding, UNLESS this PCO is all-Pending
                  (approved $0, pending > 0) — then Pending approval promotes
                  into the hero and Approved demotes into the sub-stat row
                  below, still showing its own dash there. See fundingCardLead. */}
              <span className="eyebrow">
                {fundingLead === 'pending' ? 'Pending approval' : 'Approved funding'}
              </span>
              <div className="pco-summary-hero">
                {fundingLead === 'pending'
                  ? formatMoneyCents(pending)
                  : approved === 0
                    ? '—'
                    : formatMoneyCents(approved)}
              </div>
              <div className="pco-summary-row">
                <div>
                  <span className="eyebrow">Proposed</span>
                  <div className="pco-summary-value">
                    {proposedDisplay === null ? '—' : formatMoneyCents(proposedDisplay)}
                  </div>
                </div>
                <div>
                  <span className="eyebrow">
                    {fundingLead === 'pending' ? 'Approved funding' : 'Pending approval'}
                  </span>
                  <div className="pco-summary-value">
                    {/* The `approved === 0` branch here is defensively
                        unreachable: fundingCardLead only returns 'pending'
                        when approved is already 0, so this slot always shows
                        the dash on that side. Kept explicit rather than
                        hardcoded so this stays correct if the contract ever
                        changes. */}
                    {fundingLead === 'pending'
                      ? approved === 0
                        ? '—'
                        : formatMoneyCents(approved)
                      : pending === 0
                        ? '—'
                        : formatMoneyCents(pending)}
                  </div>
                </div>
              </div>
            </div>
            <div>
              <span className="eyebrow">Submitted to date</span>
              <div className="pco-summary-hero">
                {billed === 0 ? '—' : formatMoneyCents(billed)}
              </div>
              {/* No ceiling, nothing to measure against — the bar and the
                  caption both stay off rather than drawing an empty track. */}
              {hasApprovedBudget && (
                <>
                  <p className="muted" style={{ fontSize: 12.5, margin: '6px 0 0' }}>
                    {Math.round(pctBurn)}% of approved funding
                  </p>
                  <div
                    className={`pco-bar${pctBurn > 100 ? ' is-over' : ''}`}
                    role="img"
                    aria-label={`${Math.round(pctBurn)} percent of approved funding submitted`}
                  >
                    <span style={{ width: `${Math.min(100, pctBurn)}%` }} />
                  </div>
                </>
              )}
            </div>
            {/* Its own column, level with Submitted — the two are one sentence
                read left to right ("this much has gone out, this much is
                left"), and stacking it under the bar put a wide gap beside
                the bar and a line of height under it. */}
            <div>
              <span className="eyebrow">Remaining</span>
              {/* Approved funding − submitted, server-clamped at zero and
                  shown that way on every surface (system-invariants). Over
                  budget is told by the percentage beside it, which prints the
                  real figure past 100 — not by a negative here that would
                  disagree with the PCO Log and the drawer. */}
              <div className="pco-summary-value">
                {!hasApprovedBudget || remaining === null ? '—' : formatMoneyCents(remaining)}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <p
          className="muted"
          style={{ fontSize: 13, padding: '14px 0 4px', lineHeight: 1.5 }}
        >
          No activity yet. Foreman PDFs will populate this view as they arrive.
        </p>
      )}

      {/* No needs-attention panel here (Brandon 2026-08-05, removing the
          Overview-retirement relocation the same day): the inbox already
          gates saves on unknown entities with hours and no rate, so nothing
          can silently bill — Settings is the one fix-it surface for
          unknowns, and the Dashboard banner owns failed PDFs. */}

      {/* ===== Tabs ===== */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px 16px',
          flexWrap: 'wrap',
          marginTop: 0,
          marginBottom: 20,
        }}
      >
        <div
          className="tabs"
          role="tablist"
          aria-label="PCO sections"
          style={{ marginBottom: 0 }}
        >
          <PcoTabButton
            active={activeTab === 'weekly'}
            onClick={() => setTab('weekly')}
          >
            Weekly Rollups
          </PcoTabButton>
          <PcoTabButton
            active={activeTab === 'cumulative'}
            onClick={() => setTab('cumulative')}
          >
            {/* Finding #11 — "Cumulative" didn't land with PM users; renamed to
                "Totals" (label only). Internal id/hash/route stay `cumulative`
                — renaming those isn't trivial (deep links, PCO_TAB_HASH,
                pcoTabFromHash) and isn't what this finding asked for. */}
            Totals
          </PcoTabButton>
          <PcoTabButton
            active={activeTab === 'history'}
            onClick={() => setTab('history')}
          >
            Sources
          </PcoTabButton>
        </div>
      </div>


      {/* ===== Weekly Rollups (the Active → Pending → Approved funnel) ===== */}
      {activeTab === 'weekly' && <WeeklyRollupsTab pcoId={d.pco.id} subs={subs} />}

      {/* ===== Cumulative breakdown =====
          Two-column dashboard — the View Week Details treatment (2026-08-01):
          by-* sections stack left, "Invoice totals to date" becomes a sticky
          right-hand rail card. Collapses to one column below 1100px, card at
          the bottom, same breakpoint as the week detail page (.rollup-layout
          in index.css). */}
      {activeTab === 'cumulative' && (
        <>
          {/* Funding tops the money tab (Brandon 2026-08-05, Overview
              retirement): what the GC authorized, then what we billed. */}
          <PcoFundingSection
            tranches={tranches.data ?? []}
            loading={tranches.isLoading && !tranches.data}
            amountProposedCents={proposed}
            onEditDetails={() => setEditOpen(true)}
          />
          <div className="rollup-layout">
          <div className="rollup-sections">
          {/* Detail sections fold by default — with the full project loaded
              these run long; the summary line keeps the fold informative and
              Invoice totals to date in the rail stays open as the answer. */}
          <Section
            title="Labor by employee"
            defaultExpanded={false}
            slim
            summary={`${d.labor.length} ${d.labor.length === 1 ? 'employee' : 'employees'} · ${formatMoneyCents(d.totals.labor_cents)}`}
          >
            {d.labor.length === 0 ? (
              <p className="muted" style={{ fontSize: 13 }}>
                No labor on this PCO yet.
              </p>
            ) : (() => {
              // Totals across every numeric column — treat the subtotal row as
              // an Excel SUM(), not just a $ aggregate. Sums on hour columns
              // catch row-vs-total drift Joey would otherwise have to spot by
              // eye.
              const sumSt = d.labor.reduce((a, l) => a + l.total_st_hours, 0);
              const sumOt = d.labor.reduce((a, l) => a + l.total_ot_hours, 0);
              const sumDt = d.labor.reduce((a, l) => a + l.total_dt_hours, 0);
              const sumTotalHrs = sumSt + sumOt + sumDt;
              return (
                <table className="table">
                  <thead>
                    <tr>
                      <th>Employee ID</th>
                      <th>Name</th>
                      <th>Class</th>
                      {showOtDt && <th className="num">ST hrs</th>}
                      {showOtDt && <th className="num">OT hrs</th>}
                      {showOtDt && <th className="num">DT hrs</th>}
                      <th className="num">Total hrs</th>
                      <th className="num">Total $</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.labor.map((l) => {
                      const lineHrs = l.total_st_hours + l.total_ot_hours + l.total_dt_hours;
                      return (
                        <tr key={`${l.employee_id ?? l.employee_name}`}>
                          <td className="mono" style={{ color: 'var(--ink)', fontSize: 13 }}>
                            {l.heavyjob_employee_id ?? '—'}
                          </td>
                          <td>
                            {l.employee_name}
                            {l.is_unknown && (
                              <span
                                style={{
                                  color: 'var(--warning)',
                                  marginLeft: 8,
                                  fontSize: 11,
                                }}
                              >
                                unknown
                              </span>
                            )}
                          </td>
                          <td className="muted">{l.classification}</td>
                          {showOtDt && (
                            <td className="num">{formatHours(l.total_st_hours)}</td>
                          )}
                          {showOtDt && (
                            <td className="num">{formatHours(l.total_ot_hours)}</td>
                          )}
                          {showOtDt && (
                            <td className="num">{formatHours(l.total_dt_hours)}</td>
                          )}
                          <td className="num">{formatHours(lineHrs)}</td>
                          <td className="num">{formatMoneyCents(l.total_cents)}</td>
                        </tr>
                      );
                    })}
                    <tr className="subtotal">
                      <td colSpan={3}>Labor subtotal</td>
                      {showOtDt && <td className="num">{formatHours(sumSt)}</td>}
                      {showOtDt && <td className="num">{formatHours(sumOt)}</td>}
                      {showOtDt && <td className="num">{formatHours(sumDt)}</td>}
                      <td className="num">{formatHours(sumTotalHrs)}</td>
                      <td className="num">{formatMoneyCents(d.totals.labor_cents)}</td>
                    </tr>
                  </tbody>
                </table>
              );
            })()}
          </Section>

          <Section
            title="Equipment by asset"
            defaultExpanded={false}
            slim
            summary={`${d.equipment.length} ${d.equipment.length === 1 ? 'asset' : 'assets'} · ${formatMoneyCents(d.totals.equipment_cents)}`}
          >
            {d.equipment.length === 0 ? (
              <p className="muted" style={{ fontSize: 13 }}>
                No equipment on this PCO yet.
              </p>
            ) : (() => {
              const sumHrs = d.equipment.reduce((a, e) => a + e.total_hours, 0);
              return (
                <table className="table">
                  <thead>
                    <tr>
                      <th>Asset</th>
                      <th>Description</th>
                      <th className="num">Total hours</th>
                      <th className="num">Total $</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.equipment.map((e) => (
                      <tr key={`${e.equipment_id ?? e.asset_id}`}>
                        <td className="mono" style={{ color: 'var(--ink)' }}>
                          {e.asset_id}
                          {e.is_unknown && (
                            <span
                              style={{
                                color: 'var(--warning)',
                                marginLeft: 8,
                                fontSize: 11,
                              }}
                            >
                              unknown
                            </span>
                          )}
                        </td>
                        <td className="muted">{e.description}</td>
                        <td className="num">{formatHours(e.total_hours)}</td>
                        <td className="num">{formatMoneyCents(e.total_cents)}</td>
                      </tr>
                    ))}
                    <tr className="subtotal">
                      <td colSpan={2}>Equipment subtotal</td>
                      <td className="num">{formatHours(sumHrs)}</td>
                      <td className="num">{formatMoneyCents(d.totals.equipment_cents)}</td>
                    </tr>
                  </tbody>
                </table>
              );
            })()}
          </Section>

          <Section
            title="Materials by item"
            defaultExpanded={false}
            slim
            summary={`${d.materials.length} ${d.materials.length === 1 ? 'item' : 'items'} · ${formatMoneyCents(d.totals.materials_cents)}`}
          >
            {d.materials.length === 0 ? (
              <p className="muted" style={{ fontSize: 13 }}>
                No confirmed material lines on this PCO yet.
              </p>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th className="num">Total qty</th>
                    <th className="num">Total $</th>
                  </tr>
                </thead>
                <tbody>
                  {d.materials.map((m, i) => (
                    <tr key={`${m.material_id ?? m.name}-${i}`}>
                      <td className="name serif">{m.name}</td>
                      <td className="num">{m.total_quantity}</td>
                      <td className="num">{formatMoneyCents(m.total_cents)}</td>
                    </tr>
                  ))}
                  <tr className="subtotal">
                    <td colSpan={2}>Materials subtotal</td>
                    <td className="num">{formatMoneyCents(d.totals.materials_cents)}</td>
                  </tr>
                </tbody>
              </table>
            )}
          </Section>

          {/* Vendor-invoice lumps. Unlike the sections above, these are
              rollup-level rows with no block behind them, so each invoice
              stays its own line and carries the week it billed in — that's
              what the PM matches against the sub's paperwork. */}
          <Section
            title="Subcontractors by line"
            defaultExpanded={false}
            slim
            summary={`${subcontractorSummary(d.subcontractors)} · ${formatMoneyCents(d.totals.subcontractors_cents)}`}
          >
            {d.subcontractors.length === 0 ? (
              <p className="muted" style={{ fontSize: 13 }}>
                No subcontractor lines on this PCO yet.
              </p>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>Description</th>
                    <th>Invoice date</th>
                    <th>Week</th>
                    <th className="num">Total $</th>
                  </tr>
                </thead>
                <tbody>
                  {d.subcontractors.map((s) => (
                    <tr key={s.id}>
                      <td className="name serif">{s.description}</td>
                      <td className="muted">{formatDateShort(s.invoice_date)}</td>
                      <td className="muted">{formatWeekRange(s.week_ending_date)}</td>
                      <td className="num">
                        {formatMoneyCents(s.amount_cents)}
                        {/* Same credit cue as the week page: the minus carries it,
                            never red (RULES.md §2.7); the word drops the mono
                            face (§5.1). */}
                        {s.amount_cents < 0 && (
                          <span
                            className="muted"
                            style={{ fontFamily: 'var(--sans)', fontSize: 11.5, marginLeft: 6 }}
                          >
                            credit
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                  <tr className="subtotal">
                    <td colSpan={3}>Subcontractors subtotal</td>
                    <td className="num">{formatMoneyCents(d.totals.subcontractors_cents)}</td>
                  </tr>
                </tbody>
              </table>
            )}
          </Section>
        </div>

        {/* Money rail — same card the week detail page sticks in its right
            column. Non-collapsible: this is the answer, not a detail to fold
            away. */}
        <div className="rollup-rail">
          <Section title="Invoice totals to date" collapsible={false} className="rollup-totals">
            <table className="table">
              <tbody>
                {/* Bare bucket names (Brandon 2026-08-05) — "subtotal" on
                    every line was redundant chrome; the one line that IS the
                    subtotal keeps the word. */}
                <tr>
                  <td>Labor</td>
                  <td className="num">{formatMoneyCents(d.totals.labor_cents)}</td>
                </tr>
                <tr>
                  <td>Equipment</td>
                  <td className="num">{formatMoneyCents(d.totals.equipment_cents)}</td>
                </tr>
                <tr>
                  <td>Materials</td>
                  <td className="num">{formatMoneyCents(d.totals.materials_cents)}</td>
                </tr>
                {/* Always rendered, even at $0 — same as the Materials row
                    above it. A bucket that disappears when empty makes the
                    ladder's arithmetic unverifiable at a glance. */}
                <tr>
                  <td>Subcontractors</td>
                  <td className="num">{formatMoneyCents(d.totals.subcontractors_cents)}</td>
                </tr>
                <tr>
                  <td>Subtotal</td>
                  <td className="num">{formatMoneyCents(subtotal)}</td>
                </tr>
                {d.equipment_tax_pct > 0 && (
                  <tr>
                    {/* GC wording — matches the xlsx / cover letter label. */}
                    <td>Equip. markup ({d.equipment_tax_pct}%)</td>
                    <td className="num">{formatMoneyCents(d.totals.equipment_tax_cents)}</td>
                  </tr>
                )}
                <tr>
                  <td>Markup ({d.markup_pct}%)</td>
                  <td className="num">{formatMoneyCents(d.totals.markup_cents)}</td>
                </tr>
                <tr>
                  <td>
                    Per diem (@{' '}
                    {formatMoneyCents(d.per_diem_rate_cents, { precise: true })})
                  </td>
                  <td className="num">{formatMoneyCents(d.totals.per_diem_cents)}</td>
                </tr>
                {/* What these totals cover: the ladder above sums every
                    portal-era dollar saved on this PCO — including weeks
                    still open and never sent — and "Billed before the portal"
                    is the pre-portal billing that never ran through this tool.
                    Together they are all known work to date. That is
                    deliberately NOT the same question as the "Submitted to
                    date" stat up top, which counts only what has gone out
                    to the GC: a PCO with open weeks reads higher here, and
                    the labels say so rather than pretending the two tie.

                    Row emphasis: when there's pre-portal history, "Portal
                    work to date" and "Billed before the portal" read as
                    emphasized-but-lesser (subtotal-lesser — weight 500 + a
                    top rule, no band); only "Total work to date" — the
                    figure that ties to submitted-to-date on the PCO Log —
                    gets the full accent band. With no pre-portal history
                    there's just the one row, and it carries the band. */}
                <tr className={d.imported_history_cents > 0 ? 'subtotal-lesser' : 'subtotal'}>
                  <td>
                    {d.imported_history_cents > 0 ? 'Portal work to date' : 'Total work to date'}
                  </td>
                  <td className="num">{formatMoneyCents(d.totals.invoice_total_cents)}</td>
                </tr>
                {d.imported_history_cents > 0 && (
                  <>
                    <tr className="subtotal-lesser">
                      <td>Billed before the portal</td>
                      <td className="num">{formatMoneyCents(d.imported_history_cents)}</td>
                    </tr>
                    <tr className="subtotal">
                      <td>Total work to date</td>
                      <td className="num">
                        {formatMoneyCents(d.totals.invoice_total_cents + d.imported_history_cents)}
                      </td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          </Section>
        </div>
        </div>
        </>
      )}

      {/* ===== History ===== */}
      {activeTab === 'history' && (
        <>
          {/* Finding #10 — Sources tab sections opened by default, which read
              as a wall of tables on a PCO with real history. All three fold
              closed by default now (same funnel principle as the Cumulative
              tab's Labor/Equipment/Materials); Joey still expands whichever
              one he needs. */}
          <Section
            title="Source PDFs"
            defaultExpanded={false}
            summary={
              d.contributing_pdfs.length > 0
                ? `${d.contributing_pdfs.length} PDF${d.contributing_pdfs.length === 1 ? '' : 's'}`
                : undefined
            }
          >
            {d.contributing_pdfs.length === 0 ? (
              <p className="muted" style={{ fontSize: 13 }}>
                No source PDFs — this PCO has no entries yet.
              </p>
            ) : (
              <>
                <SourcePdfsTable
                  pdfs={d.contributing_pdfs}
                  generatedByDaily={generatedByDaily}
                  generatedStatus={generatedStatus}
                  onPreviewPdf={setPreviewPdfId}
                  onCompare={setComparePair}
                />
                {/* The Generated column is the only thing this fetch feeds, so
                    its failure is reported here rather than over the whole
                    tab — the rest of the table is still true. */}
                {generatedStatus === 'error' && (
                  <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>
                    Couldn’t load the generated copies, so that column is blank —
                    it isn’t saying these reports were never sent.{' '}
                    <button
                      type="button"
                      className="btn-link"
                      onClick={() => void generatedDocs.refetch()}
                    >
                      Retry
                    </button>
                  </p>
                )}
              </>
            )}
          </Section>

          {/* Read-only. Code→PCO mapping is configuration — it's changed only
              in Settings → PCOs (forward-only). The one activity-codes card
              on the page since the Overview tab retired (2026-08-05). */}
          <Section
            title="Activity codes mapped to this PCO"
            defaultExpanded={false}
            summary={
              d.mapped_activity_codes.length > 0
                ? `${d.mapped_activity_codes.length} code${d.mapped_activity_codes.length === 1 ? '' : 's'} mapped`
                : undefined
            }
          >
            {d.mapped_activity_codes.length === 0 ? (
              <p className="muted" style={{ fontSize: 13 }}>
                No activity codes mapped yet.{' '}
                <button
                  type="button"
                  className="btn-link"
                  onClick={() => navigate('/settings#pcos')}
                >
                  Map codes in Settings
                  <ArrowRight size={13} aria-hidden />
                </button>
              </p>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Description from PDF</th>
                  </tr>
                </thead>
                <tbody>
                  {d.mapped_activity_codes.map((c) => (
                    <tr key={c.id}>
                      <td className="mono">{c.code}</td>
                      <td className="muted">{c.description_from_pdf ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Section>

          {/* Reconciliation — stub until the standalone /reconciliation route
              is decommissioned and the per-PCO variance data lands here for
              real. Empty state for now; the framing is what matters: when a
              variance shows up, this is where it lives.

              TODO(F-018): populated state per docs/Design_Redesign_Brief.md —
              table of line item / Coffman invoiced / Hoffman approved /
              variance / status, with --danger color on a non-zero variance
              row. Promote the per-row "Resolve →" affordance from the
              attention panel. */}
          <Section title="Reconciliation" defaultExpanded={false}>
            <p className="muted" style={{ fontSize: 13 }}>
              No variances against {gcName(me)}'s recorded amounts. When a mismatch
              between what we billed and what {gcName(me)} recorded shows up, it'll
              appear here with a link to the affected rollup.
            </p>
          </Section>
        </>
      )}

      {/* ===== Modals (always mounted; visibility driven by their `open` props) ===== */}
      <PdfPreviewModal source={previewPdfId} onClose={() => setPreviewPdfId(null)} />

      {/* Original left, what Hoffman actually received right. Same component
          the inbox duplicate check uses — the question is the same shape ("how
          do these two pages differ?") and answering it by toggling one viewer
          means holding the first document in your head. */}
      {comparePair && (
        <PdfCompareModal
          left={{
            pdfId: comparePair.dailyPdfId,
            filename: comparePair.filename,
            badge: 'original',
          }}
          right={{
            pdfId: { kind: 'generated', id: comparePair.artifactId },
            filename: comparePair.filename,
            badge: `sent to ${gcName(me)}`,
          }}
          onClose={() => setComparePair(null)}
        />
      )}

      <EditPcoModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        tranches={tranches.data ?? []}
        tranchesLoading={tranches.isLoading && !tranches.data}
        onRefetch={async () => {
          // Reconcile page money + the tranche list with the DB WITHOUT
          // closing the modal — the failure-recovery path in EditPcoModal.
          await Promise.all([
            cumulative.refetch(),
            rollups.refetch(),
            settingsPcos.refetch(),
            tranches.refetch(),
            subs.refetch(),
          ]);
        }}
        initial={{
          id: d.pco.id,
          pco_number: d.pco.pco_number,
          description: d.pco.description,
          status: d.pco.status,
          // From the settings-PCO row (the detail payload doesn't carry it).
          // null while that list is still loading — the modal then omits the
          // field on save rather than guessing, so a slow list can never flip
          // a weekly-rollup PCO to lump-sum behind the PM's back.
          billing_type: pcoMoney?.billing_type ?? null,
          // Same three-state contract, same reason: `undefined` while the
          // settings-PCO row is unread, because a stored null here means
          // "already retired" and the save acts on the difference. `??` would
          // collapse the two and retire a figure nothing ever looked at.
          amount_proposed_cents: pcoMoney ? pcoMoney.amount_proposed_cents : undefined,
          comments: pcoMoney?.comments ?? null,
          void_reason: pcoMoney?.void_reason ?? null,
          is_no_cost: pcoMoney?.is_no_cost ?? false,
        }}
        onUpdated={async () => {
          setEditOpen(false);
          // The rail renders from AppShell's own PCO list — refresh it too or
          // a rename sits stale in the nav until the next navigation.
          refetchNav();
          await Promise.all([
            cumulative.refetch(),
            rollups.refetch(),
            settingsPcos.refetch(),
            tranches.refetch(),
            subs.refetch(),
          ]);
        }}
      />
    </Container>
  );
}

function PcoTabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      className={active ? 'active' : ''}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

/**
 * The Sources tab's PDF table.
 *
 * TWO DOCUMENT COLUMNS SINCE 2026-08-02. "Filename" became **Original PDF** and
 * gained **Generated PDF** beside it, because a regenerated daily is a different
 * page from the one the foreman signed — scoped to this PCO, corrections in red,
 * the code normalized — and until now nothing in the product could show it. The
 * View action opens BOTH when there is a generated copy, and the original alone
 * when there isn't, so the button never has to be read twice.
 *
 * MIRRORED ONTO THE WEEK PAGE 2026-08-06 — RollupDetailPage.tsx's
 * SourceBlocksSection carries the same two columns for one week's own blocks,
 * additionally filtered to that week's rollup (a block can move between
 * weeks; this table's own PCO-wide artifacts never need that filter, because
 * every reader of THIS tab is already looking at the whole PCO).
 */
function SourcePdfsTable({
  pdfs,
  generatedByDaily,
  generatedStatus,
  onPreviewPdf,
  onCompare,
}: {
  pdfs: PcoCumulativeContributingPdf[];
  generatedByDaily: Map<string, PcoGeneratedDocument>;
  generatedStatus: GeneratedColumnStatus;
  onPreviewPdf: (id: string) => void;
  onCompare: (pair: SourceComparePair) => void;
}) {
  return (
    <table className="table">
      <thead>
        <tr>
          <th>Report date</th>
          <th>Foreman</th>
          <th>Original PDF</th>
          <th>Generated PDF</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {pdfs.map((p) => {
          const filename = derivePdfFilename({
            email_subject: p.email_subject,
            foreman_name: p.foreman_name,
            report_date: p.report_date,
          });
          const cell = generatedDocumentCell(generatedByDaily.get(p.daily_pdf_id));
          return (
            <tr key={p.daily_pdf_id}>
              <td className="muted">{formatDateShort(p.report_date)}</td>
              <td className="serif">{p.foreman_name ?? '—'}</td>
              <td className="mono" style={{ fontSize: 12 }}>{filename}</td>
              <td className="muted" style={{ fontSize: 12 }}>
                {generatedStatus !== 'ready' ? (
                  ''
                ) : (
                  <>
                    {/* Dated because the reasons beside it are recomputed from
                        today's data while the page itself is frozen bytes. */}
                    {cell.generatedAt && `${formatDateMd(cell.generatedAt.slice(0, 10))} · `}
                    {cell.label}
                  </>
                )}
              </td>
              <td className="num">
                <button
                  type="button"
                  className="btn-link btn-link-strong"
                  onClick={() =>
                    cell.artifactId
                      ? onCompare({
                          dailyPdfId: p.daily_pdf_id,
                          artifactId: cell.artifactId,
                          filename,
                        })
                      : onPreviewPdf(p.daily_pdf_id)
                  }
                >
                  View PDF
                  <ArrowRight size={13} aria-hidden />
                </button>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function PcoStatusButton({
  pcoId,
  pcoNumber,
  currentStatus,
  isComplete,
  onChanged,
}: {
  pcoId: string;
  pcoNumber: string;
  currentStatus: PcoStatus;
  isComplete: boolean;
  onChanged: () => Promise<void>;
}) {
  const { token } = useAuth();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Reopen returns the PCO to 'open' (the neutral active-family value); the
  // legacy 'active' status is retired from write paths (funding buckets #8).
  const targetStatus: PcoStatus = isComplete ? 'open' : 'paid';
  const label = isComplete ? 'Reopen' : 'Mark complete';

  const doChange = async () => {
    if (!token) return;
    setBusy(true);
    setErr(null);
    try {
      await api.settings.patchPcoStatus(token, pcoId, targetStatus);
      setConfirmOpen(false);
      await onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : `${label} failed`);
    } finally {
      setBusy(false);
    }
  };

  void currentStatus; // satisfies the lint without changing behavior; currentStatus is captured via isComplete

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      {err && <span style={{ color: 'var(--danger)', fontSize: 12 }}>{err}</span>}
      {/* Mark PCO complete is the primary terminal action on this header;
          Edit details sits as ghost to its left. Reopen flips to ghost
          when the PCO is already complete since the action then deletes a
          terminal state and shouldn't read as a primary CTA. */}
      <button
        type="button"
        className={isComplete ? 'btn btn-ghost' : 'btn btn-cta'}
        onClick={() => setConfirmOpen(true)}
        disabled={busy}
      >
        {isComplete ? 'Reopen PCO' : 'Mark PCO complete'}
      </button>

      <ConfirmModal
        open={confirmOpen}
        title={isComplete ? `Reopen PCO ${formatPcoNumber(pcoNumber)}?` : `Mark PCO ${formatPcoNumber(pcoNumber)} complete?`}
        body={
          isComplete
            ? 'The PCO will move back to your Active list.'
            : "It moves from Active to Completed. You can reopen any time — this is just a label."
        }
        confirmLabel={isComplete ? 'Reopen PCO' : 'Mark complete'}
        busyLabel="Saving…"
        busy={busy}
        onConfirm={doChange}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
}

function Section({
  title,
  summary,
  defaultExpanded = true,
  collapsible = true,
  slim = false,
  className,
  children,
}: {
  title: string;
  /** Muted line beside the title — keeps a folded section informative
   *  ("23 employees · $4,205,120.00") so opening it is a choice, not a
   *  requirement. */
  summary?: string;
  /** Initial expanded state. Defaults to true so the page paints with
   *  full detail; Joey collapses sections he wants to skim past. */
  defaultExpanded?: boolean;
  /** Set false for a static block (no toggle). Used for the Totals tab's
   *  "Invoice totals to date" rail card — same as the week detail page's
   *  Invoice totals card, which never collapses. */
  collapsible?: boolean;
  /** Tighter collapsed-row chrome (`section--card--slim`), matching the
   *  week detail page's rhythm. Used by the Totals tab's by-* sections now
   *  that they sit in a two-column layout; other tabs keep the roomier card. */
  slim?: boolean;
  /** Extra class(es) appended to the card div — e.g. `rollup-totals` for the
   *  Totals tab's rail card. */
  className?: string;
  children: React.ReactNode;
}) {
  const [expanded, setExpanded] = useState<boolean>(defaultExpanded);
  const showBody = collapsible ? expanded : true;
  const cardClass = [
    'section',
    'section--card',
    slim ? 'section--card--slim' : null,
    slim && expanded ? 'is-expanded' : null,
    className ?? null,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={cardClass}>
      {collapsible ? (
        <div
          className="section-head clickable"
          role="button"
          tabIndex={0}
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setExpanded((v) => !v);
            }
          }}
          title={expanded ? 'Click to collapse' : 'Click to expand'}
          style={expanded ? undefined : { marginBottom: 0 }}
        >
          <div className="section-head-left">
            <span style={{ color: 'var(--ink-3)', display: 'inline-flex' }}>
              <ExpandChevron expanded={expanded} size={14} />
            </span>
            <h2 className="section-title">{title}</h2>
            {summary && (
              <span className="muted" style={{ fontSize: 13, marginLeft: 10 }}>
                {summary}
              </span>
            )}
          </div>
        </div>
      ) : (
        <div className="section-head">
          <h2 className="section-title">{title}</h2>
          {summary && (
            <span className="muted" style={{ fontSize: 13 }}>
              {summary}
            </span>
          )}
        </div>
      )}
      {showBody && children}
    </div>
  );
}

void ACTIVE_STATUSES; // kept exported-like to anchor the "active vs complete" semantics

/**
 * "2 invoices" — or, once a vendor credit is on the PCO (a negative line,
 * 2026-09-25), "2 invoices, 1 credit". A PCO with no credits reads as before.
 */
function subcontractorSummary(lines: ReadonlyArray<{ amount_cents: number }>): string {
  const credits = lines.filter((l) => l.amount_cents < 0).length;
  const invoices = lines.length - credits;
  const inv = `${invoices} ${invoices === 1 ? 'invoice' : 'invoices'}`;
  return credits === 0 ? inv : `${inv}, ${credits} ${credits === 1 ? 'credit' : 'credits'}`;
}
